import { useEffect, useRef, useState } from 'react';
import {
  getBroadcastCoordinator,
  destroyBroadcastCoordinator,
  type BroadcastMessage,
} from '../broadcast-coordinator';
import type { DataChangedEvent } from './use-realtime-sync';

const DATA_CHANGED_EVENT = 'data_changed';

interface UseBroadcastCoordinatorProps {
  handleDataChanged: (event: DataChangedEvent) => void;
  unwrapEventData: (data: unknown) => DataChangedEvent | null;
}

interface UseBroadcastCoordinatorReturn {
  isLeader: boolean;
  coordinatorRef: React.RefObject<ReturnType<
    typeof getBroadcastCoordinator
  > | null>;
}

export function useBroadcastCoordinator({
  handleDataChanged,
  unwrapEventData,
}: UseBroadcastCoordinatorProps): UseBroadcastCoordinatorReturn {
  const coordinatorRef = useRef<ReturnType<
    typeof getBroadcastCoordinator
  > | null>(null);
  const [isLeader, setIsLeader] = useState(false);

  useEffect(() => {
    coordinatorRef.current = getBroadcastCoordinator();
    setIsLeader(coordinatorRef.current.getIsLeader());

    // Listen for leadership changes and broadcast messages
    coordinatorRef.current.onMessage(
      'provider',
      (message: BroadcastMessage) => {
        if (message.type === 'leader-announce') {
          const newIsLeader =
            message.tabId === coordinatorRef.current?.getTabId();
          setIsLeader(newIsLeader);
        } else if (message.type === 'websocket-message') {
          // Follower tabs receive WebSocket messages via broadcast
          if (
            !coordinatorRef.current?.getIsLeader() &&
            message.event === DATA_CHANGED_EVENT
          ) {
            const eventData = unwrapEventData(message.data);
            if (eventData) {
              handleDataChanged(eventData);
            }
          }
        }
      }
    );

    return () => {
      destroyBroadcastCoordinator();
    };
  }, [handleDataChanged, unwrapEventData]);

  return {
    isLeader,
    coordinatorRef,
  };
}

/**
 * Broadcast a WebSocket message to other tabs
 */
export function broadcastToFollowers(
  coordinatorRef: React.RefObject<ReturnType<
    typeof getBroadcastCoordinator
  > | null>,
  channel: string,
  event: string,
  data: unknown
) {
  coordinatorRef.current?.broadcastWebSocketMessage(channel, event, data);
}
